<template>
  <not-found-page
    v-if="!loading && typeof schedule.uid === 'undefined'"
    :to="{ name: routeName.updateSchedules }"
    :title="$t('notFoundPage.title', { type: $t('notFoundPage.checkInSchedule')})"
    :anchor-text="$t('notFoundPage.buttonText', { types: $t('navigation.updateSchedules')})"
  />
  <page-layout
    v-else
    :class="['schedule-detail-page', modal ? '-modal' : '', isDirty ? '-dirty' : '']"
    :loading="loading"
    :modal="modal"
  >
    <template
      #topBar
    >
      <schedule-detail-top
        :notification="schedule"
        :modal="modal"
        :can-edit="canEdit"
      />
    </template>
    <scroll-container>
      <m-content
        boxed-small
        padding-x="layout"
        class="_content"
      >
        <div class="_title">
          <editable-page-header
            :key="pageHeaderKey"
            size="small"
            :title="localValue.title"
            :icon="icon"
            :show-icon="!isEmptyIcon(icon) || emojiAdded"
            :auto-focus="schedule.title === ''"
            :placeholder="$t('general.untitled')"
            :disabled="!canEdit"
            show-icon-on-top
            class="_title"
            @change-icon="changeIcon"
            @change-title="changeTitle"
          >
            <template #actions>
              <div class="_header-actions">
                <m-btn
                  v-if="isEmptyIcon(icon) && !emojiAdded && canEdit"
                  hide-border
                  class="_item"
                  icon="smile"
                  light
                  small
                  @click="addEmoji"
                >
                  {{ $t('page.addEmoji') }}
                </m-btn>
              </div>
            </template>
          </editable-page-header>
        </div>
        <div class="_properties">
          <property-label
            :label="$t('schedulesTable.status')"
            icon="poweroff"
            :class="['_prop', !canEdit ? '-disabled' : '']"
          >
            <m-dropdown
              block
              match-trigger-width
              placement="onTopLeft"
              :title="$t('schedulesTable.status')"
              :disabled="!canEdit"
            >
              <div class="_value">
                <div class="_status">
                  <m-tag
                    :color="statusType"
                    class="_tag"
                    :title="statusTitle"
                  />
                </div>
              </div>
              <template #overlay>
                <m-card
                  class="_overlay"
                  no-padding
                  border-radius="small"
                >
                  <div class="_top">
                    <div
                      class="_status"
                      :style="{ display: 'inline-flex' }"
                    >
                      <m-tag
                        :color="statusType"
                        class="_tag"
                        :title="statusLabel"
                      />
                    </div>
                  </div>
                  <div
                    class="_list"
                    :style="{ padding: '.4rem 0' }"
                  >
                    <m-card-item
                      v-for="s in status"
                      :key="s.value"
                      class="_status"
                      @click="changeStatus(s.value)"
                    >
                      <m-tag
                        :color="s.type"
                        class="_tag"
                        :title="s.title"
                      />
                    </m-card-item>
                  </div>
                </m-card>
              </template>
            </m-dropdown>
          </property-label>
          <property-label
            :label="$t('schedulesTable.schedule')"
            icon="calendar"
            :class="['_prop']"
          >
            <m-dropdown
              placement="onTopLeft"
              :title="$t('schedulesTable.schedule')"
              match-trigger-width
              block
            >
              <div class="_value -rrule">
                {{ rruleText }}
              </div>
              <template #overlay>
                <m-card
                  border-radius="small"
                  class="_overlay -schedule"
                  no-padding
                >
                  <div class="_top">
                    {{ rruleText }}
                  </div>
                  <r-rule-picker
                    :value="localValue.schedule"
                    class="_overlay-content"
                    hide-error
                    :disabled="!canEdit"
                    @input="changeSchedule"
                  />
                  <m-content
                    padding-xs
                    :class="['_hint', notificationUnsure ? '-warning' : '']"
                  >
                    <template v-if="nextNotification === null">
                      {{ $t('scheduleDetailPage.noSendOut') }}
                    </template>
                    <template v-else>
                      <m-icon
                        v-if="notificationUnsure"
                        type="warning"
                        :color="$colors.yellow.darken1"
                        class="_icon"
                      />
                      <span>
                        {{
                          $t('scheduleDetailPage.nextSendOut', { date: nextNotification.toLocaleString(DateTime.DATETIME_MED) })
                        }}
                        <template v-if="notificationUnsure">
                          {{ $t('scheduleDetailPage.sendOutHint') }}
                        </template>
                      </span>
                    </template>
                  </m-content>
                </m-card>
              </template>
            </m-dropdown>
          </property-label>
          <property-label
            :label="$t('schedulesTable.participants')"
            icon="team"
            :class="['_prop', !canEdit ? '-disabled' : '']"
          >
            <m-dropdown
              v-model:value="showRecipients"
              block
              placement="onTopLeft"
              :title="$t('schedulesTable.participants')"
              :disabled="!canEdit"
            >
              <div class="_value">
                {{ `${recipientsCount} ${$t('userScopeTreeUserField.persons', recipientsCount)}` }}
              </div>
              <template #overlay>
                <m-card
                  border-radius="small"
                  no-padding
                  class="_overlay"
                >
                  <user-scope-tree-user-field
                    :class="['_overlay-content -recipients', $store.state.breakpoint.mdAndUp ? '-not-mobile' : '']"
                    :value="recipients"
                    :disabled="!canEdit"
                    @input="updateRecipients"
                    @close="showRecipients = false"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </property-label>
          <property-label
            :label="$t('scheduleDetailPage.reminders')"
            icon="bell"
            :class="['_prop', !canEdit ? '-disabled' : '']"
          >
            <m-dropdown
              block
              match-trigger-width
              placement="onTopLeft"
              :title="$t('scheduleDetailPage.reminders')"
              :disabled="!canEdit"
            >
              <div class="_value">
                <div
                  v-if="filteredReminders.length === 0"
                  class="_empty"
                >
                  {{ $t('scheduleDetailPage.noReminders') }}
                </div>
                <reminder-item
                  v-for="(item, index) in filteredReminders"
                  :key="index"
                  :reminder="item"
                  disabled
                  component="div"
                />
              </div>
              <template #overlay>
                <m-card
                  no-padding
                  border-radius="small"
                  list
                  placement="onTopLeft"
                  class="_overlay"
                >
                  <reminder-list
                    :reminders="filteredReminders"
                    :disabled="!canEdit"
                    @create="addReminder"
                    @update="updateReminder"
                    @delete="deleteReminder"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </property-label>
          <property-label
            :label="$t('schedulesTable.template')"
            icon="read"
            :class="['_prop', !canEdit ? '-disabled' : '']"
          >
            <m-dropdown
              block
              match-trigger-width
              placement="onTopLeft"
              :title="$t('schedulesTable.template')"
              :disabled="!canEdit"
            >
              <div class="_value">
                <template v-if="localValue.updateTemplate !== null">
                  <item-title
                    :title="localValue.updateTemplate.title"
                    :icons="[{ value: buildIconFromEntity(localValue.updateTemplate) }]"
                  />
                </template>
                <div
                  v-else
                  class="_empty"
                >
                  {{ $t('updateTemplateSelector.noTemplateSelected') }}
                </div>
              </div>
              <template #overlay>
                <m-card
                  border-radius="small"
                  class="_overlay"
                  no-padding
                >
                  <div class="_top">
                    <template v-if="localValue.updateTemplate !== null">
                      <item-title
                        :title="localValue.updateTemplate.title"
                        :icons="[{ value: buildIconFromEntity(localValue.updateTemplate) }]"
                      />
                    </template>
                    <div
                      v-else
                      class="_is-empty"
                    >
                      {{ $t('updateTemplateSelector.noTemplateSelected') }}
                    </div>
                  </div>
                  <template-list
                    :style="{ padding: '.4rem 0' }"
                    @close="changeTemplate"
                    @delete-template="deleteTemplate"
                    @select="changeTemplate"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </property-label>
        </div>
        <m-divider />
        <div class="_participation-rate">
          <div class="_header">
            <div class="_title">
              {{ $t('scheduleDetailPage.participationRate') }}
            </div>
            <div class="_range">
              <m-select
                :items="chartMonthsOptions"
                :value="chartMonths"
                btn
                small
                hide-border
                light
                @input="chartMonths = $event"
              />
            </div>
          </div>
          <participation-chart
            :chart-data="chartData"
            :min-x="chartMinX"
            :highlight-x="chartHighlightX"
            @point-click="selectParticipationPoint"
          />
        </div>
        <div class="_participants-list">
          <participants-table
            v-if="Object.keys(schedule).length > 0"
            v-model:selected-date="selectedParticipationTime"
            :participation-list="schedule.updateParticipationList ?? []"
            :occurrences="occurrences"
          />
        </div>
      </m-content>
    </scroll-container>
    <m-content
      v-if="isDirty && canEdit"
      padding-x="layout"
      boxed-small
      class="_footer"
    >
      <div class="_footer-inner">
        <m-btn
          class="_btn"
          hide-border
          @click="cancel"
        >
          {{ $t('general.cancel') }}
        </m-btn>
        <m-btn
          class="_btn"
          color="primary"
          :loading="updateLoading"
          @click="save"
        >
          {{ $t('general.save') }}
        </m-btn>
      </div>
    </m-content>
  </page-layout>
</template>

<script setup>
import EditablePageHeader from '@/components/page/EditablePageHeader.vue';
import ItemTitle from '@/components/ItemTitle.vue';
import NotFoundPage from '@/components/NotFoundPage.vue';
import PageLayout from '@/components/page/PageLayout.vue';
import ParticipantsTable from '@/components/update-schedules/ParticipantsTable.vue';
import ParticipationChart from '@/components/update-schedules/ParticipationChart.vue';
import PropertyLabel from '@/components/PropertyLabel.vue';
import RRulePicker from '@/components/RRulePicker.vue';
import ReminderItem from '@/components/update-schedules/ReminderItem.vue';
import ReminderList from '@/components/update-schedules/ReminderList.vue';
import ScheduleDetailTop from '@/components/update-schedules/ScheduleDetailTop.vue';
import ScrollContainer from '@/components/page/ScrollContainer.vue';
import TemplateList from '@/components/updates/TemplateList.vue';
import UserScopeTreeUserField from '@/components/UserScopeTreeUserField.vue';
import useRRuleTranslations from '@/composables/rrule-translations/translations';
import useSnackBar from '@/composables/snackbar';
import useUpdateSchedules from '@/composables/update-schedules/update-schedules';
import { DateTime } from 'luxon';
import { EventBus } from '@/lib/event-bus';
import { accessPolicyType, customFunc, routeName } from 'shared/constants.json';
import { buildIconFromEntity, isEmptyIcon } from 'shared/lib/icon';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { getStatus, getStatusType } from '@/lib/notification/notification';
import { isEqual } from 'lodash-es';
import { logCatch } from '@/lib/logger/logger';
import { notificationChildren } from '@/api/query/nebula/notification';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { rrulestr } from 'rrule';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  scheduleId: {
    type: Number,
    required: true,
  },
  modal: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const snackbar = useSnackBar();
const { updateSchedule, updateLoading, selectSingle } = useUpdateSchedules();
const { rruleToText } = useRRuleTranslations();

const loading = ref(true);
const chartMonths = ref(3);
const emojiAdded = ref(false);
const showRecipients = ref(false);
const pageHeaderKey = ref(0);
const recipientsCount = ref(0);
const recipients = ref(null);
const initialValue = ref(null);
const reminders = ref([]);
const localValue = ref({
  title: '',
  icon: '',
  activatedAt: undefined,
  schedule: '',
  updateTemplate: null,
});

const schedule = computed(() => selectSingle(props.scheduleId) ?? {});
const icon = computed(() => buildIconFromEntity(localValue.value));
const canEdit = computed(() => [accessPolicyType.write, accessPolicyType.full].includes(schedule.value.accessRight));
const filteredReminders = computed(() => reminders.value.filter((r) => r.deletedAt === undefined));

const editableValues = (source) => ({
  title: source.title,
  icon: source.icon,
  activatedAt: source.activatedAt,
  schedule: source.schedule,
  updateTemplate: source.updateTemplate ? { uid: source.updateTemplate.uid } : null,
});

const isDirty = computed(() => {
  if (initialValue.value === null) {
    return false;
  }

  return !isEqual(editableValues(localValue.value), editableValues(initialValue.value))
    || !isEqual(reminders.value, initialValue.value.reminders)
    || !isEqual(recipients.value, initialValue.value.recipients);
});

const nextNotification = computed(() => {
  const rule = rrulestr(localValue.value.schedule);
  const now = (new Date());
  now.setMinutes(-now.getTimezoneOffset());
  const next = rule.after(now);
  if (next === null) {
    return null;
  }

  return DateTime.fromJSDate(next)
    .toUTC()
    .setZone('local', { keepLocalTime: true });
});

const notificationUnsure = computed(() => {
  if (nextNotification.value === null || !isDirty.value) {
    return false;
  }
  const d = nextNotification.value.toJSDate();
  const n = new Date();
  return ((d - n) / (60 * 1000)) < 20;
});

const chartMinX = ref(null);
const selectedParticipationTime = ref(null);

const chartHighlightX = computed(() => {
  const participationList = schedule.value[customFunc.updateParticipationList];
  if (participationList === undefined || participationList.length === 0) {
    return null;
  }

  const entry = participationList.find((p) => p.time === selectedParticipationTime.value) ?? participationList[0];
  return Date.parse(entry.time);
});

const chartMonthsOptions = computed(() => [3, 6, 12].map((months) => ({
  text: t('scheduleDetailPage.lastMonths', { months }),
  value: months,
})));

const status = computed(() => [
  {
    title: t('notificationItem.paused'),
    value: 'paused',
    type: 'warning',
  },
  {
    title: t('propertyConstants.active'),
    value: 'active',
    type: 'success',
  },
]);

const amount = (participation) => {
  if (participation.numberOfRecipients === 0) {
    return '0/0';
  }
  return `${participation.numberOfParticipants}/${participation.numberOfRecipients}`;
};

const chartData = computed(() => (schedule.value[customFunc.updateParticipationList] ?? []).map((p) => ({
  x: Date.parse(p.time),
  y: Math.round((p.numberOfParticipants / p.numberOfRecipients) * 100),
  participation: amount(p),
})));

const rruleText = computed(() => {
  try {
    const rule = rrulestr(localValue.value.schedule);
    return rruleToText(rule, { includeTime: true });
  } catch (e) {
    return '';
  }
});

const isPaused = computed(() => typeof schedule.value.activatedAt !== 'undefined' && schedule.value.activatedAt === null);

const anchor = computed(() => {
  if (!isPaused.value || !schedule.value.pausedAt) {
    return DateTime.local();
  }

  return DateTime.fromISO(schedule.value.pausedAt);
});

const statusType = computed(() => getStatusType({ activatedAt: localValue.value.activatedAt }));
const statusLabel = computed(() => getStatus({ activatedAt: localValue.value.activatedAt, $t: t }));
const statusTitle = computed(() => {
  if (localValue.value.activatedAt === null && isPaused.value && schedule.value.pausedAt) {
    return t('scheduleDetailPage.pausedAt', { date: DateTime.fromISO(schedule.value.pausedAt).toLocaleString(DateTime.DATE_MED) });
  }

  return statusLabel.value;
});

const chartBoundaries = computed(() => {
  try {
    const rule = rrulestr(schedule.value.schedule);
    let next = rule.after(anchor.value.toJSDate());
    if (next === null) {
      next = anchor.value.toJSDate();
    }

    const rangeStart = rule.before(anchor.value.minus({ months: chartMonths.value }).toJSDate(), true) ?? rule.options.dtstart;
    const from = rule.options.dtstart > rangeStart ? rule.options.dtstart : rangeStart;
    return rule.between(from, next, true)
      .map((b) => DateTime.fromJSDate(b)
        .toUTC()
        .setZone('local', { keepLocalTime: true }));
  } catch (e) {
    return [];
  }
});

const occurrences = computed(() => {
  try {
    const rule = rrulestr(schedule.value.schedule);
    const boundaries = chartBoundaries.value;

    if (boundaries.length === 1) {
      const n = rule.after(boundaries[0].plus({ days: 1 }).toJSDate(), false);
      const periodEnd = n !== null
        ? DateTime.fromJSDate(n).minus({ minute: 1 })
        : DateTime.local();
      return boundaries.map((b) => ({
        text: `${b.toLocaleString(DateTime.DATETIME_MED)} - ${periodEnd.toLocaleString(DateTime.DATETIME_MED)}`,
        value: periodEnd.toISO({ suppressMilliseconds: true }),
      }));
    }

    const windows = boundaries.filter((_, index) => typeof boundaries[index + 1] !== 'undefined')
      .map((b, i) => ({
        text: `${b.toLocaleString(DateTime.DATETIME_MED)} - ${boundaries[i + 1].minus({ minute: 1 }).toLocaleString(DateTime.DATETIME_MED)}`,
        value: boundaries[i + 1].minus({ minutes: 1 }).toISO({ suppressMilliseconds: true }),
      }));

    windows.reverse();
    return windows;
  } catch (e) {
    return [];
  }
});

const timeSeries = computed(() => {
  if (isPaused.value && schedule.value.pausedAt) {
    return occurrences.value.map((next) => DateTime.fromISO(next.value).toISO());
  }

  const now = DateTime.local();
  return occurrences.value.reduce((res, next) => {
    const n = DateTime.fromISO(next.value);
    if (n.diff(now) > 0) {
      return res;
    }

    res.push(n.toISO());
    return res;
  }, [now.toISO()]);
});

const participationRangeStart = computed(() => chartBoundaries.value[0]?.toISO({ suppressMilliseconds: true }) ?? '');

const setValues = () => {
  localValue.value = {
    title: initialValue.value.title,
    icon: initialValue.value.icon,
    activatedAt: initialValue.value.activatedAt,
    schedule: initialValue.value.schedule,
    updateTemplate: initialValue.value.updateTemplate === null ? null : copy(initialValue.value.updateTemplate),
  };
  reminders.value = copy(initialValue.value.reminders);
  recipients.value = copy(initialValue.value.recipients);
  recipientsCount.value = initialValue.value.recipientsList.length;
};

const addEmoji = () => {
  emojiAdded.value = true;
};

const cancel = () => {
  setValues();
  pageHeaderKey.value += 1;
};

const addReminder = (reminder) => {
  reminders.value.push(reminder);
};

const updateReminder = (reminder, index) => {
  reminders.value.splice(index, 1, reminder);
};

const deleteReminder = (index) => {
  if (Number.isNaN(reminders.value[index].uid)) {
    reminders.value.splice(index, 1);
    return;
  }

  reminders.value.splice(index, 1, {
    ...reminders.value[index],
    deletedAt: DateTime.local().toISO(),
  });
};

const deleteTemplate = (template) => {
  if (localValue.value.updateTemplate === null || localValue.value.updateTemplate.uid !== template.uid) {
    return;
  }
  localValue.value.updateTemplate = null;
};

const changeStatus = (value) => {
  switch (value) {
    case 'paused':
      localValue.value.activatedAt = null;
      break;
    case 'active':
      localValue.value.activatedAt = DateTime.local().toISO();
      break;
    default:
  }
};

const changeTemplate = (template) => {
  localValue.value.updateTemplate = template;
};

const changeIcon = (value) => {
  localValue.value.icon = value.value;
};

const changeTitle = (value) => {
  localValue.value.title = value;
};

const changeSchedule = (value) => {
  localValue.value.schedule = value;
};

const updateRecipients = ({ userScopeTree, count }) => {
  recipientsCount.value = count;
  if (!canEdit.value || isEqual(userScopeTree, recipients.value)) {
    return;
  }

  recipients.value = userScopeTree;
};

const get = () => {
  const scheduleAvailable = typeof schedule.value.schedule !== 'undefined';

  loading.value = true;
  return selectSingle(
    props.scheduleId,
    { commitToRemote: true, attributes: notificationChildren(timeSeries.value, participationRangeStart.value) },
  ).then(() => {
    if (typeof schedule.value.uid === 'undefined') {
      return undefined;
    }

    initialValue.value = copy(schedule.value);
    setValues();

    if (scheduleAvailable) {
      return undefined;
    }

    return get();
  }).catch(logCatch(() => {
    snackbar.error();
  }))
    .finally(() => {
      loading.value = false;
    });
};

const save = () => {
  const changes = { uid: schedule.value.uid, ...localValue.value, reminders: reminders.value };
  if (!isEqual(recipients.value, initialValue.value.recipients)) {
    changes.recipients = recipients.value;
  }

  const request = updateSchedule(changes, notificationChildren(timeSeries.value, participationRangeStart.value)).then(() => {
    initialValue.value = copy(schedule.value);
    setValues();
    snackbar.success(t('success.saved'));
  });

  request.catch(() => {
    snackbar.error();
  });

  return request;
};

const addressesSchedule = (route) => [route.query.notificationId, route.params.notificationId]
  .some((id) => parseInt(id, 10) === props.scheduleId);

const handleRouteChange = (to, from, next) => {
  if (!isDirty.value || addressesSchedule(to)) {
    next();
    return;
  }

  EventBus.$emit(
    'show-confirm',
    {
      title: t('general.discardEditPrompt'),
      cancelText: t('general.save'),
      okText: t('general.discardChanges'),
      okType: 'warning',
      cancelType: 'default',
      maskClosable: true,
      onOk: () => {
        cancel();
        next();
      },
      onCancel: (setLoading) => {
        setLoading(true);
        save().then(() => {
          setLoading(false);
          next();
        }).catch(() => {
          setLoading(false);
        });
      },
    },
  );
};
onBeforeRouteLeave(handleRouteChange);
onBeforeRouteUpdate(handleRouteChange);

watch(() => props.scheduleId, get, { immediate: true });

watch(chartMonths, () => {
  selectSingle(
    props.scheduleId,
    { commitToRemote: true, attributes: notificationChildren(timeSeries.value, participationRangeStart.value) },
  );
});

watch(occurrences, (val) => {
  selectedParticipationTime.value = val[0]?.value ?? null;
}, { immediate: true });

const selectParticipationPoint = (x) => {
  const occurrence = occurrences.value.find((o) => Date.parse(o.value) === x) ?? occurrences.value[0];
  if (occurrence !== undefined) {
    selectedParticipationTime.value = occurrence.value;
  }
};

watch(() => schedule.value[customFunc.updateParticipationList], () => {
  chartMinX.value = anchor.value.minus({ months: chartMonths.value }).toMillis();
}, { immediate: true });
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  @import "shared/assets/scss/padding";

  $value-padding: .4rem 1.1rem;

  .schedule-detail-page {
    position: relative;

    ._content {
      ._participation-rate {
        margin-top: 5rem;

        ._header {
          display: flex;
          margin-bottom: 1.2rem;

          ._title {
            display: flex;
            align-items: center;
            font-weight: $font-weight-bold;
          }

          ._range {
            display: flex;
            margin-left: auto;
          }
        }
      }

      ._status {
        display: inline-flex;
      }

      ._participants-list {
        margin-top: 5rem;
        margin-bottom: 8rem;
      }

      ._properties {
        ._prop {
          ._value {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
            padding: $value-padding;
            border: 1px solid transparent;
            border-radius: $input-field-border-radius;

            ._selected-emoji {
              margin-right: .6rem;
            }

            ._empty {
              color: $font-color-tertiary;
            }
          }

          &:not(.-disabled) {
            ._value {
              cursor: pointer;

              &:hover {
                background-color: $hover-color;
              }
            }
          }
        }
      }
    }

    &.-modal {
      height: calc(var(--viewport-height-100) - 14.4rem);
    }

    $footer-height: 6.5rem;

    ._footer {
      /* this overrides the z-index of the m-select>m-focusable within the participants-table component */
      z-index: 2;
      position: absolute;
      bottom: 0;
      left: 0;
      display: flex;
      align-items: center;
      width: 100%;
      height: $footer-height;
      background-color: white;
      border-top: 1px solid $border-color;
      animation: fade-in .5s ease-in-out;

      @include layoutPaddingX();

      @keyframes fade-in {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }

      ._footer-inner {
        display: flex;
        justify-content: flex-end;
        width: 100%;

        ._btn {
          margin-left: .6rem;
        }
      }
    }
  }

  ._overlay {
    border: 1px solid $input-border-color;

    ._top {
      display: flex;
      align-items: center;
      padding: $value-padding;
      background-color: map_get($grey, 'lighten-4');
      border-bottom: 1px solid $input-border-color;
      border-top-left-radius: $input-field-border-radius;
      border-top-right-radius: $input-field-border-radius;

      ._is-empty {
        color: $font-color-tertiary;
      }
    }

    ._overlay-content {
      padding: $value-padding;

      &.-recipients {
        padding: 0;

        &.-not-mobile {
          max-width: 57rem;
        }
      }
    }

    &.-schedule {
      ._hint {
        &.-warning {
          color: $warning-color;

          ._icon {
            display: inline-block;
            margin-right: .4rem;
          }
        }
      }
    }
  }
</style>
